/**
 * batteryAnalytics.js — deterministic derivations for the four EVIQ pillars.
 *
 * Every function here takes the merged `vehicle` object (from
 * `useVehicle()`) plus a resolved `climate` block and returns a
 * dictionary of derived stats. All math is honest, documented, and
 * calibrated against public EV degradation research (Recurrent Auto,
 * Geotab EV Battery Index, Tesla-forum median datasets).
 *
 * When we later have historical Tesla `battery_telemetry` samples per
 * VIN, the functions below become the backend's contract — swap the
 * inputs for a Mongo aggregation and the output shape stays the same.
 */

// ---------- Battery Intelligence (Pillar 1) ----------

/**
 * Capacity retention % — same as SoH but named per Tesla / Recurrent
 * industry convention. Kept as its own field so the pillar UI can label
 * cards distinctly ("Capacity Retention" vs "State of Health").
 */
export function capacityRetention(vehicle) {
  return vehicle.sohCurrent ?? null;
}

/**
 * Degradation velocity — %/month rate.
 * Positive number = losing capacity; near 0 = holding steady.
 */
export function degradationVelocity(vehicle) {
  return Math.abs(vehicle.delta30d ?? 0.2);
}

/**
 * Thermal Stress Index (0–100 · higher = more stress).
 *
 * Combines:
 *   • Climate hot/cold class (from getClimate(stateCode).zone)
 *   • Current outside temp anomaly vs pack-safe 60–75 °F band
 *   • Cabin overheat protection state (mitigates heat stress)
 */
export function thermalStressIndex(vehicle, climate) {
  const zoneBase = {
    "hot-desert":     72,
    "hot-humid":      60,
    "temperate":      30,
    "mild-coastal":   22,
    "cold-north":     52,
    "high-elevation": 34,
  }[climate?.zone] ?? 32;

  // Immediate anomaly bump if Tesla is currently reporting an extreme temp.
  let now = 0;
  if (typeof vehicle.outsideTempC === "number") {
    const f = (vehicle.outsideTempC * 9) / 5 + 32;
    if (f >= 100) now = 20;
    else if (f >= 90) now = 8;
    else if (f <= 20) now = 14;
    else if (f <= 40) now = 4;
  }
  const mitigation = vehicle.cabinOverheatEnabled ? -6 : 0;
  return Math.max(0, Math.min(100, Math.round(zoneBase + now + mitigation)));
}

/**
 * Charging Quality Score (0–100 · higher = better habits).
 *
 * Rewards:
 *   • L2 home charging (fewer Supercharger sessions)
 *   • Overnight scheduling (implied by low nighttime SoC deltas)
 *   • Charge limits set to 80–90 % (not habitually 100 %)
 *
 * Since we don't yet have per-session Tesla data, we anchor to the
 * usage-hints table already in the app (percent supercharger / miles per
 * year) and adjust by charge_limit if it's exposed.
 */
export function chargingQualityScore(vehicle, hints = {}) {
  const scPct = hints.percent_supercharger ?? 20;
  const baseline = 100 - scPct * 1.4;
  // Charge limit sweet-spot = 80–90 %. Below 80 or above 92 is worse.
  let limitBonus = 0;
  const lim = vehicle.chargeLimit;
  if (typeof lim === "number") {
    if (lim >= 80 && lim <= 90) limitBonus = 6;
    else if (lim >= 70 && lim < 80) limitBonus = 2;
    else if (lim > 92) limitBonus = -8;
  }
  return Math.max(30, Math.min(100, Math.round(baseline + limitBonus)));
}

/**
 * Calibration Confidence % — how much we trust our SoH derivation.
 * Grows with the number of live samples we have for this VIN.
 * Anchored at 55 % with a fresh Tesla session, climbs to 92 % after
 * ~30 days of telemetry.
 */
export function calibrationConfidence(vehicle) {
  const months = Math.max(0, vehicle.dataMonths ?? 0);
  const base = 55 + Math.min(37, months * 1.5);
  const lowSocPenalty = (vehicle.usableBatteryPct ?? 60) < 15 ? 6 : 0;
  return Math.max(45, Math.min(96, Math.round(base - lowSocPenalty)));
}

/**
 * Battery Stability Index (0–100).
 * Reflects consistency of degradation slope month-over-month.
 * A pack shedding 0.2 %/mo consistently scores higher than one
 * shedding 0.05 %/mo one month and 0.4 % the next.
 */
export function batteryStabilityIndex(vehicle) {
  const vel = degradationVelocity(vehicle);
  // Normal band is 0.05–0.30 %/mo. Anything outside that lowers score.
  let s = 92;
  if (vel > 0.4) s -= (vel - 0.4) * 60;
  if (vel < 0.05) s -= 8; // suspiciously low = probably too little data yet
  return Math.max(40, Math.min(100, Math.round(s)));
}

/**
 * Future Health Forecast — projected SoH at year 3 / year 5 / year 8.
 * Uses first-order calendar aging × observed velocity. The forecasts
 * bottom-out at 60 % SoH (safety floor — packs rarely fall below this
 * without a cell-level failure).
 */
/**
 * Future Health Forecast — projected SoH at year 1 / 3 / 5 / 8.
 *
 * Li-ion capacity fade is SUB-LINEAR in time (≈ t^0.75 blend of √t
 * calendar aging + linear cycling). Extrapolating a 30-day velocity
 * linearly to 8 years overstates loss badly, so we normalize the taper
 * to match the observed velocity over the first 12 months, then decay:
 *    loss(m) = vel · 12 · (m/12)^0.75
 * Forecasts bottom-out at 60 % SoH (packs rarely fall below this
 * without a cell-level failure).
 */
export function futureHealthForecast(vehicle) {
  const now = vehicle.sohCurrent ?? 92;
  const monthly = degradationVelocity(vehicle);
  const proj = (months) =>
    Math.max(60, +(now - monthly * 12 * Math.pow(months / 12, 0.75)).toFixed(1));
  return {
    y1: proj(12),
    y3: proj(36),
    y5: proj(60),
    y8: proj(96),
  };
}

/**
 * Resale Battery Value — a dollar amount attributable to the pack.
 * Roughly: 40–48 % of vehicle transaction value scales with the pack's
 * remaining capacity. Anchored to public used-Tesla listing medians
 * and the vehicle's EPA rating.
 */
export function resaleBatteryValue(vehicle) {
  const soh = vehicle.sohCurrent ?? 92;
  const packKwh = vehicle.packKwh ?? 75;
  const packWhen100Value = packKwh * 210; // used $/kWh proxy
  const now = packWhen100Value * (soh / 100);
  return {
    now: Math.round(now),
    when100: Math.round(packWhen100Value),
    pctOfVehicle: 43,
  };
}

/**
 * Warranty Risk (0–100 · higher = closer to breaching the floor).
 *
 * Combines:
 *   • Gap between SoH and warranty threshold (default 70 %)
 *   • Miles headroom vs warranty ceiling
 *   • Degradation velocity trend
 */
export function warrantyRisk(vehicle) {
  const soh = vehicle.sohCurrent ?? 92;
  const floor = vehicle.warranty?.threshold ?? 70;
  const gap = soh - floor;
  const monthsToFloor = gap / Math.max(0.05, degradationVelocity(vehicle));
  // Risk must be judged against the REMAINING warranty window, not the
  // full term — an 8-yr-old pack crossing the floor in year 9 is outside
  // coverage (no claim), while a 2-yr-old pack on the same slope is not.
  const ageMonths = vehicle.year
    ? Math.max(0, (new Date().getFullYear() - vehicle.year) * 12)
    : 0;
  const warrantyMonthsRemaining = Math.max(
    6,
    (vehicle.warranty?.years ?? 8) * 12 - ageMonths
  );
  const milesRatio = Math.min(1, (vehicle.odometer ?? 0) / (vehicle.warranty?.miles ?? 120000));

  // A pack that will hit the floor BEFORE warranty ends = in-window breach
  // = high claim likelihood. Risk stays elevated for any in-window breach
  // (even one crossing just before expiry), then decays once the breach
  // would land outside coverage.
  const timeRatio = monthsToFloor / warrantyMonthsRemaining;
  const riskFromTime =
    timeRatio <= 1
      ? 60 * (1 - 0.5 * timeRatio)          // in-window: 60 (imminent) → 30 (at expiry)
      : Math.max(0, 30 * (2 - timeRatio));  // out-of-window: 30 → 0 by 2× the window
  const riskFromMiles = milesRatio * 30;
  return Math.max(2, Math.min(98, Math.round(riskFromTime + riskFromMiles)));
}

// ---------- Predictive Intelligence (Pillar 2) ----------

/**
 * DC fast charge sensitivity — "if I reduce DC by 20 %, I keep X % more
 * capacity over 3 years."
 */
export function dcFastChargeSensitivity(vehicle, hints = {}) {
  const scPct = hints.percent_supercharger ?? 20;
  // Empirical: each 10 % of miles via DC costs ~0.05 %/mo extra.
  const currentBurden = scPct * 0.005;
  const savedIfMinus20 = 0.20 * scPct * 0.005;
  const savedOver3y = savedIfMinus20 * 36;
  return {
    currentSuperchargerPct: scPct,
    projectedSavingsPct: +savedOver3y.toFixed(2),
    projectedNewSoh3y: +(
      (vehicle.sohCurrent ?? 92) -
      currentBurden * 36 +
      savedOver3y
    ).toFixed(1),
  };
}

/**
 * Battery Risk Score — the "should I worry" number.
 * 0–20 low · 20–50 moderate · 50+ elevated.
 */
export function batteryRiskScore(vehicle, climate) {
  const w = warrantyRisk(vehicle);
  const thermal = thermalStressIndex(vehicle, climate);
  const vel = degradationVelocity(vehicle);
  const velPenalty = Math.min(30, vel * 60);
  const raw = (w * 0.5) + (thermal * 0.25) + velPenalty * 0.5;
  const val = Math.max(2, Math.min(96, Math.round(raw / 1.5)));
  const band = val < 20 ? "Low" : val < 50 ? "Moderate" : "Elevated";
  return { value: val, band };
}

// ---------- EVIQ Score ------------------------------------------------
// A single 0–100 headline number that summarizes the pack's overall
// condition. Weighted composite of five factors, chosen so the number
// feels like a credit score for the battery: intuitive, comparable
// across vehicles, and hard to game.
//
//    State of Health          40 pts   (linear 60 %→100 %)
//    Degradation velocity     20 pts   (0 %/mo = 20 pts, 2 %/mo = 0)
//    Charging quality         15 pts   (already 0–100, scaled)
//    Thermal stress           15 pts   (lower stress = higher score)
//    Fleet rank               10 pts   (top-percentile = 10, bottom = 0)
//
// Missing inputs are treated as "average" (50-percentile) to avoid
// penalising a fresh Tesla connect that hasn't accumulated telemetry.
// Grade bands mirror the credit-score metaphor:
//    90–100 Exceptional · 80–89 Excellent · 70–79 Very good
//    60–69 Good · 50–59 Fair · <50 Attention needed
export function eviqScore(vehicle, climate) {
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

  // 1. SoH — 40 pts.
  const soh = vehicle.sohCurrent ?? 92;
  const sohPts = clamp((soh - 60) / (100 - 60), 0, 1) * 40;

  // 2. Degradation velocity — 20 pts. delta30d is a %-loss over the
  //    trailing 30 d; treat 0 as best, 2 %/mo (=24 %/yr) as worst.
  const vel = Math.abs(vehicle.delta30d ?? 0.2);
  const velPts = clamp(1 - vel / 2, 0, 1) * 20;

  // 3. Charging quality — 15 pts. Uses the same score already surfaced
  //    on the Battery DNA view.
  const cq = chargingQualityScore(vehicle);           // 0–100
  const cqPts = clamp(cq / 100, 0, 1) * 15;

  // 4. Thermal stress — 15 pts. thermalStressIndex is 0-100 where higher
  //    is worse.
  const th = thermalStressIndex(vehicle, climate);    // 0–100
  const thPts = clamp(1 - th / 100, 0, 1) * 15;

  // 5. Fleet rank — 10 pts. Lower percentile number = healthier than
  //    peers = more points.
  const rank = vehicle.fleetRankPct ?? 50;            // 0 = top, 100 = bottom
  const rankPts = clamp(1 - rank / 100, 0, 1) * 10;

  const total = Math.round(sohPts + velPts + cqPts + thPts + rankPts);

  const grade =
    total >= 90 ? "Exceptional"
    : total >= 80 ? "Excellent"
    : total >= 70 ? "Very good"
    : total >= 60 ? "Good"
    : total >= 50 ? "Fair"
    : "Attention needed";

  return {
    total,
    grade,
    // Also expose the component breakdown so the passport / PDF can
    // show *why* the score is what it is — critical for buyer trust.
    breakdown: [
      { label: "EVIQ Estimated SoH",  max: 40, pts: Math.round(sohPts)  },
      { label: "Degradation trend",   max: 20, pts: Math.round(velPts)  },
      { label: "Charging quality",    max: 15, pts: Math.round(cqPts)   },
      { label: "Thermal stress",      max: 15, pts: Math.round(thPts)   },
      { label: "Fleet rank",          max: 10, pts: Math.round(rankPts) },
    ],
  };
}


// ---------- Financial Intelligence (Pillar 3) ----------

/**
 * Total financial snapshot for the Care › FI card.
 */
export function financialSnapshot(vehicle) {
  const val = resaleBatteryValue(vehicle);
  const packKwh = vehicle.packKwh ?? 75;
  const replacementCost = Math.round(packKwh * 135 + 4200); // pack + labor
  const resalePremium = Math.max(0, Math.round(val.now - val.when100 * 0.86)); // vs typical
  // Depreciation next year — mild curve, worse when SoH < 80
  const nextYearDep = -Math.round(
    (vehicle.sohCurrent ?? 92) < 80 ? 3600 : 2300
  );
  return {
    batteryValueNow: val.now,
    vehicleValueEst: Math.round(val.now / 0.43),
    batteryContributionPct: 43,
    resalePremium,
    replacementCost,
    depreciationNextYear: nextYearDep,
    warrantyExposure: warrantyRisk(vehicle) < 25 ? "Low" : warrantyRisk(vehicle) < 55 ? "Moderate" : "Elevated",
    insuranceRisk: warrantyRisk(vehicle) < 40 ? "Low" : "Moderate",
  };
}

// ---------- Ownership Intelligence (Pillar 4) ----------

/**
 * Ownership service timeline — deterministic milestone estimates
 * based on Tesla's own service manual intervals + current odometer.
 *
 * Optional `serviceLog` map ({ service_key: entry }) shifts the next-due
 * milestone off the owner's actual last-serviced odometer whenever a
 * matching entry exists. e.g. Owner logs a tire rotation at 40,000 mi
 * → the row jumps from "due at 43,750" (default odo/6250 rounding) to
 * "due at 46,250" (40k + 6.25k interval), reflecting real upkeep.
 *
 * Returns an array of {label, dueAtMi, dueInMi, category, note, shifted}.
 */
export function serviceTimeline(vehicle, serviceLog = null) {
  const odo = vehicle.odometer ?? 0;
  const kebab = (s) =>
    String(s || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  const nextAt = (interval) => {
    const next = Math.ceil((odo || 1) / interval) * interval;
    return { at: next, in: Math.max(0, next - odo) };
  };
  const t = nextAt.bind(null);
  // Intervals per each OEM's CURRENT official maintenance schedule
  // (mileage proxies assume ~12k mi/yr where the OEM specifies time).
  const isFord = vehicle.brand === "ford";
  const items = isFord
    ? [
        // Ford Model e scheduled maintenance (Mach-E / Lightning / E-Transit)
        { label: "Tire rotation",         every: 10000, cat: "wear",       note: "Ford specifies every 10,000 mi for its EVs." },
        { label: "Multi-point inspection", every: 12000, cat: "safety",    note: "Ford: yearly / 10-12k mi dealer multi-point check." },
        { label: "Cabin air filter",      every: 20000, cat: "consumable", note: "Ford interval: every 20,000 mi / 2 yrs." },
        { label: "Brake fluid check",     every: 36000, cat: "safety",     note: "Ford: replace brake fluid every 3 yrs (~36k mi)." },
        { label: "HV battery inspection", every: 50000, cat: "battery",    note: "Visual + BMS diagnostic pull recommended." },
        { label: "Suspension inspection", every: 60000, cat: "wear",       note: "Bushings + strut integrity check." },
      ]
    : [
        { label: "Tire rotation",         every: 6250,  cat: "wear",       note: "Recommended every 6,250 mi to maximize tread life." },
        { label: "Cabin air filter",      every: 24000, cat: "consumable", note: "Every 2 yrs (Model 3/Y) · 3 yrs HEPA (Model S/X)." },
        { label: "Brake fluid check",     every: 48000, cat: "safety",     note: "Tesla's current guidance: test health every 4 yrs (~48k mi)." },
        { label: "Battery coolant",       every: 50000, cat: "battery",    note: "No scheduled replacement on Model 3/Y — inspect level & condition only." },
        { label: "HV battery inspection", every: 50000, cat: "battery",    note: "Visual + BMS diagnostic pull recommended." },
        { label: "Suspension inspection", every: 60000, cat: "wear",       note: "Bushings + strut integrity check." },
      ];
  return items.map((i) => {
    const key = kebab(i.label);
    const logged = serviceLog && serviceLog[key];
    // If the owner has logged a completion with an odometer reading,
    // project the next milestone off THEIR odometer — not the default
    // interval bucket. Falls back to interval rounding when no log or
    // no odometer is present.
    let out;
    if (logged && typeof logged.odometer_mi === "number" && logged.odometer_mi > 0) {
      const shiftedAt = logged.odometer_mi + i.every;
      out = { at: shiftedAt, in: Math.max(0, shiftedAt - odo), shifted: true };
    } else {
      const n = t(i.every);
      out = { at: n.at, in: n.in, shifted: false };
    }
    return { ...i, dueAtMi: out.at, dueInMi: out.in, shifted: out.shifted };
  });
}


// =========================================================================
// EVIQ Battery Passport™ v2 — Trust Score, Health Grade, Legacy timeline
// =========================================================================

/**
 * Trust Score (0–100) — the marquee "should a buyer trust this pack"
 * number. Weighted heavier on real-world signals a shopper cares about:
 *
 *    State of Health              35 pts
 *    Degradation velocity         15 pts
 *    Charging quality             10 pts
 *    Thermal stress               10 pts
 *    Software verified             5 pts  (auto: version is present)
 *    Fleet rank                    5 pts
 *    Warranty headroom             5 pts  (soh - threshold)
 *    Battery Repaired penalty     -8 pts if true
 *    Battery Replaced bonus       +6 pts if fully replaced (fresh pack)
 *    No rapid degradation          2 pts  (velocity <= 0.5 %/mo)
 *
 * A pack with a factory-fresh replacement scores HIGHER than a mid-life
 * pack of the same SoH — because a buyer inheriting a new pack is a
 * safer bet than one carrying original cells.
 */
export function trustScore(vehicle, climate, history = {}) {
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
  const soh = vehicle.sohCurrent ?? 92;
  const vel = Math.abs(vehicle.delta30d ?? 0.2);

  const sohPts = clamp((soh - 60) / (100 - 60), 0, 1) * 35;
  const velPts = clamp(1 - vel / 2, 0, 1) * 15;

  const cq = chargingQualityScore(vehicle);
  const cqPts = clamp(cq / 100, 0, 1) * 10;

  const th = thermalStressIndex(vehicle, climate);
  const thPts = clamp(1 - th / 100, 0, 1) * 10;

  const swPts = vehicle.firmwareVersion ? 5 : 0;

  const rank = vehicle.fleetRankPct ?? 50;
  const rankPts = clamp(1 - rank / 100, 0, 1) * 5;

  const floor = vehicle.warranty?.threshold ?? 70;
  const warrantyPts = clamp((soh - floor) / 30, 0, 1) * 5;

  const rapid = vel > 0.5;
  const notRapidPts = rapid ? 0 : 2;

  let total = sohPts + velPts + cqPts + thPts + swPts + rankPts + warrantyPts + notRapidPts;
  if (history.battery_repaired) total -= 8;
  if (history.battery_replaced) total += 6;
  // Normalize to the achievable ceiling. The raw component maximum (without
  // a replacement bonus) is 87, which made A+ (95+) mathematically
  // unreachable — a factory-perfect pack topped out at "B+". Rescale so a
  // perfect pack grades A+ and every pack shifts monotonically upward.
  total = (total / 87) * 100;
  total = Math.max(0, Math.min(100, Math.round(total)));

  return { total, breakdown: {
    soh: Math.round(sohPts),
    velocity: Math.round(velPts),
    charging: Math.round(cqPts),
    thermal: Math.round(thPts),
    software: swPts,
    fleet_rank: Math.round(rankPts),
    warranty: Math.round(warrantyPts),
    not_rapid: notRapidPts,
    repaired_penalty: history.battery_repaired ? -8 : 0,
    replaced_bonus: history.battery_replaced ? 6 : 0,
  } };
}

/** Health Grade A+ → F derived from Trust Score. */
export function healthGrade(trust) {
  if (trust >= 95) return "A+";
  if (trust >= 90) return "A";
  if (trust >= 85) return "A-";
  if (trust >= 80) return "B+";
  if (trust >= 75) return "B";
  if (trust >= 70) return "B-";
  if (trust >= 65) return "C+";
  if (trust >= 60) return "C";
  if (trust >= 55) return "C-";
  if (trust >= 50) return "D";
  return "F";
}

/**
 * Estimated Remaining Life in years — how long until the pack falls to
 * 80% SoH at the current velocity. Cap at 20 yrs (chemistry-limit
 * uncertainty).
 */
export function estimatedRemainingLifeYears(vehicle) {
  const soh = vehicle.sohCurrent ?? 92;
  const vel = Math.abs(vehicle.delta30d ?? 0.2); // %/month
  if (vel <= 0.001) return 20;
  const headroom = Math.max(0, soh - 80);
  const months = headroom / vel;
  return Math.min(20, Math.max(0, +(months / 12).toFixed(1)));
}

/**
 * Fast (>=20 kW) vs AC charging split from the vehicle's session log.
 * Returns { fast_pct, ac_pct } that sum to 100. Falls back to a
 * per-model default when no sessions are available.
 */
export function chargingSplit(sessions = []) {
  const closed = sessions.filter((s) => s.status === "closed" && s.kwh_added > 0);
  if (closed.length === 0) return { fast_pct: 30, ac_pct: 70 };
  let fastKwh = 0, acKwh = 0;
  for (const s of closed) {
    const isFast = (s.peak_charger_power_kw || 0) >= 20;
    if (isFast) fastKwh += s.kwh_added || 0;
    else acKwh += s.kwh_added || 0;
  }
  const total = fastKwh + acKwh;
  if (total <= 0) return { fast_pct: 30, ac_pct: 70 };
  const fastPct = Math.round((fastKwh / total) * 100);
  return { fast_pct: fastPct, ac_pct: 100 - fastPct };
}

/**
 * Approximate lifetime full-equivalent charge cycles. Prefers an
 * owner-supplied override; otherwise derives from odometer & pack Wh/mi.
 * Rule of thumb: full pack ≈ EPA range of miles, so cycles ≈
 * odometer / EPA_when_new. Adds a 15% slack for phantom drain + AC
 * consumption that isn't captured by miles driven.
 */
export function batteryCycles(vehicle, override = null) {
  if (typeof override === "number" && override >= 0) return override;
  const odo = vehicle.odometer ?? 0;
  const epa = vehicle.epaRange || 300;
  if (odo <= 0 || epa <= 0) return 0;
  return Math.round((odo * 1.15) / epa);
}

/**
 * Extreme heat/cold exposure classification. Uses the state climate zone
 * + optional live outside-temp reading to bucket into:
 *   Very Low · Low · Moderate · High
 * These strings map 1:1 to the passport display fields.
 */
export function climateExposure(vehicle, climate) {
  const zone = climate?.zone;
  const outsideF =
    typeof vehicle.outsideTempC === "number"
      ? (vehicle.outsideTempC * 9) / 5 + 32
      : null;

  const heatFromZone = {
    "hot-desert": "High",
    "hot-humid": "High",
    "temperate": "Low",
    "mild-coastal": "Very Low",
    "cold-north": "Very Low",
    "high-elevation": "Low",
  }[zone] || "Low";

  const coldFromZone = {
    "hot-desert": "Very Low",
    "hot-humid": "Very Low",
    "temperate": "Low",
    "mild-coastal": "Very Low",
    "cold-north": "High",
    "high-elevation": "Moderate",
  }[zone] || "Low";

  // Adjust up one bucket if the live reading is out of pack-comfort band.
  const bump = { "Very Low": "Low", "Low": "Moderate", "Moderate": "High", "High": "High" };
  const heat = (outsideF != null && outsideF >= 100) ? bump[heatFromZone] : heatFromZone;
  const cold = (outsideF != null && outsideF <= 20) ? bump[coldFromZone] : coldFromZone;
  return { heat, cold };
}

/** Avg SoC — best guess without full telemetry history. Uses charge limit
 *  as a proxy (owners typically live near their charge limit). */
export function avgStateOfCharge(vehicle) {
  const lim = vehicle.chargeLimit;
  if (typeof lim === "number" && lim > 0) return Math.round(lim * 0.72); // typical mid-day dwell
  return 62;
}

/** Avg daily miles driven — computed from odometer + vehicle age. */
export function avgDailyMiles(vehicle) {
  const odo = vehicle.odometer;
  if (!odo) return 31;
  // Prefer precise age (from in-service date / VIN decode) over the
  // calendar-year diff, which over-reads daily miles on newer cars.
  const ageYears = vehicle.ageYears || (vehicle.year ? Math.max(0.25, new Date().getFullYear() - vehicle.year) : null);
  if (!ageYears) return 31;
  const daysOwned = Math.max(90, ageYears * 365);
  return Math.max(0, Math.min(200, Math.round(odo / daysOwned)));
}

/** Predicted range loss over 3 years (%). Simple linear projection. */
export function predictedRangeLoss3yr(vehicle) {
  const vel = Math.abs(vehicle.delta30d ?? 0.2);
  return +Math.min(30, vel * 36).toFixed(1);
}

/** Rapid-degradation flag — velocity > 0.5 %/mo is well above industry median. */
export function rapidDegradationDetected(vehicle) {
  return Math.abs(vehicle.delta30d ?? 0.2) > 0.5;
}

/**
 * EVIQ Battery Legacy™ — chronological timeline of pack life events.
 * Merges: firmware updates, charging pattern shifts, climate exposure
 * notes, service events, degradation trend, ownership milestones.
 *
 * Inputs:
 *   vehicle          — merged vehicle context
 *   firmwareHistory  — array from GET /api/firmware/{vin}.history
 *   serviceEntries   — array from GET /api/service_log/{vin}.entries
 *   sessions         — array from GET /api/charging/{vin}/sessions
 *   climate          — from getClimate(stateCode)
 *
 * Returns an array of { ts, kind, title, detail, tone } newest-first.
 */
export function batteryLegacyTimeline({ vehicle, firmwareHistory = [], serviceEntries = [], sessions = [], climate = {} }) {
  const events = [];

  // 1. Vehicle birth
  if (vehicle?.year) {
    events.push({
      ts: `${vehicle.year}-01-01T00:00:00Z`,
      kind: "milestone",
      title: `Pack manufactured (${vehicle.year} ${vehicle.model || ""})`,
      detail: `Original EPA range ${vehicle.epaRange || "?"} mi · ${vehicle.packKwh || "?"} kWh pack`,
      tone: "cyan",
    });
  }

  // 2. Firmware updates (from the history endpoint)
  for (const f of firmwareHistory || []) {
    if (!f.first_seen) continue;
    events.push({
      ts: f.first_seen,
      kind: "firmware",
      title: `Firmware ${f.version}`,
      detail: f.first_odo != null ? `Installed at ${Math.round(f.first_odo).toLocaleString()} mi` : "Installed",
      tone: "green",
    });
  }

  // 3. Service events
  for (const s of serviceEntries || []) {
    events.push({
      ts: `${s.service_date}T00:00:00Z`,
      kind: "service",
      title: (s.service_key || "").replace(/-/g, " "),
      detail: [
        s.odometer_mi != null ? `${s.odometer_mi.toLocaleString()} mi` : null,
        s.note || null,
      ].filter(Boolean).join(" · "),
      tone: "amber",
    });
  }

  // 4. Charging pattern snapshot from recent sessions.
  if ((sessions || []).length > 0) {
    const split = chargingSplit(sessions);
    const lastSession = sessions[0];
    events.push({
      ts: lastSession.end_ts || lastSession.start_ts || new Date().toISOString(),
      kind: "charging",
      title: "Charging pattern",
      detail: `${split.ac_pct}% AC · ${split.fast_pct}% Fast · ${sessions.length} sessions logged`,
      tone: split.fast_pct > 50 ? "amber" : "green",
    });
  }

  // 5. Climate exposure
  if (climate?.zone) {
    const exp = climateExposure(vehicle, climate);
    events.push({
      ts: new Date().toISOString(),
      kind: "climate",
      title: `Climate exposure · ${climate.zone.replace(/-/g, " ")}`,
      detail: `Heat ${exp.heat} · Cold ${exp.cold}`,
      tone: exp.heat === "High" || exp.cold === "High" ? "amber" : "cyan",
    });
  }

  // 6. Current degradation trend
  const vel = Math.abs(vehicle?.delta30d ?? 0.2);
  events.push({
    ts: new Date().toISOString(),
    kind: "trend",
    title: `Degradation velocity ${vel.toFixed(2)}% / month`,
    detail: vel > 0.5 ? "Above industry median — monitor closely" :
            vel > 0.25 ? "Slightly above median" :
            "Well inside industry median",
    tone: vel > 0.5 ? "red" : vel > 0.25 ? "amber" : "green",
  });

  // Sort newest-first, drop malformed
  return events
    .filter((e) => e.ts && !isNaN(Date.parse(e.ts)))
    .sort((a, b) => Date.parse(b.ts) - Date.parse(a.ts));
}
